import axios from "axios";
import { clearUser } from "../../store/auth/userSlice"; // Assuming you have this action to clear user data
import { setLoadingUsers, setSelectedUser } from "../../store/modules/accountManagement/usersSlice";

const handleUnauthorized = (dispatch, navigate) => {
	dispatch(clearUser());
	navigate("/login");
};

export const createUser = async (data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};

	try {
		const response = await axios.post(`${import.meta.env.VITE_API_BACKEND}api/users`, data, config);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 422) {
			// Return the validation error from the response
			return { errors: error.response.data.errors };
		} else if (error.response && error.response.status === 400) {
			return { errors: { Error: [error.response.data.message] } };
		} else {
			console.error("Error creating user:", error);
		}
		return null;
	}
};

export const fetchUsers = async (
	token,
	dispatch,
	navigate,
	page,
	perPage,
	paginate,
	searchTerms,
	signal,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			page: page,
			per_page: perPage,
			paginate: paginate,
			role: searchTerms?.role || null,
			status: searchTerms?.status || null,
			businessUnits: searchTerms?.businessUnits || null,
			clients: searchTerms?.clients || null,
			distributor: searchTerms?.distributor || null,
			search: searchTerms?.search || null,
		},
		signal,
	};

	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/users`, config);

		let data;
		if (!response.data.data || response.data.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
				page: paginate === 1 ? page : 1,
				per_page: paginate === 1 ? perPage : 10,
			};
		} else {
			const totalItems = response.data?.total;
			data = {
				data: response.data.data,
				totalItems: totalItems,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		}
		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching Users data:", error);
		}
	}
};

export const fetchUserById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/users/${id}`, config);

		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching users data:", error);
		}
	}
};

export const fetchUserDetails = async (id, token, dispatch, navigate) => {
	await dispatch(setSelectedUser(id));
	await dispatch(setLoadingUsers(true));

	try {
		const data = await fetchUserById(id, token, dispatch, navigate);
		dispatch(setSelectedUser(data));
	} catch (error) {
		console.error("Error fetching data:", error);
	} finally {
		dispatch(setLoadingUsers(false));
	}
};

export const resendRegisterEmail = async (id, token) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/users/${id}/resend`,
			{},
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			console.error("Unauthorized. Invalid token.");
			return { errors: ["Unauthorized. Invalid token."] };
		} else if (error.response && error.response.status === 422) {
			return { errors: error.response.data.errors };
		} else {
			console.error("Error sending token:", error);
			return { errors: ["Error sending token."] };
		}
	}
};

export const deleteUserById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.delete(
			`${import.meta.env.VITE_API_BACKEND}api/users/${id}`,
			config,
		);
		if (response.status === 200 || response.status === 202 || response.status === 204) {
			return {
				status: "success",
				message: "User deleted successfully.",
			};
		} else {
			return {
				status: "error",
				message: response.data?.message || "An unexpected error occurred.",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 404:
					return { status: "not_found", message: "Not found." };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				case 400:
					return {
						status: "error",
						message: error.response.data?.message || "An error occurred.",
					};
				default:
					return { status: "error", message: "An error occurred." };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};
export const updateUser = async (id, data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	try {
		const response = await axios.put(
			`${import.meta.env.VITE_API_BACKEND}api/users/${id}`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 422) {
			return { errors: error.response.data.errors };
		} else if (error.response && error.response.status === 400) {
			return { errors: { Error: [error.response.data.message] } };
		} else {
			console.error("Error updating user:", error);
		}
		return null;
	}
};

export const setUpdateProfilePhoto = async (id, formData, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/users/profile/${id}`,
			formData,
			config,
		);

		if (response.status === 200 || response.status === 201) {
			return { status: "success", data: response.data.data.photo };
		} else {
			return {
				status: "error",
				message: "Unexpected server response",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 422:
					return {
						status: "unprocessable_entity",
						errors: error.response.data.errors,
					};
				case 404:
					return { status: "not_found", message: "Resource not found" };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				default:
					return { status: "error", message: "An error occurred" };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};

export const setUpdateProfileInformations = async (id, formData, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`, // Only include necessary headers
		},
	};

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/users/profile/${id}`,
			formData,
			config,
		);
		if (response.status === 200 || response.status === 201) {
			return { status: "success", data: response.data.data };
		} else {
			return {
				status: "error",
				message: "Unexpected server response",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 422:
					return {
						status: "unprocessable_entity",
						errors: error.response.data.errors,
					};
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				default:
					return { status: "error", message: "An error occurred" };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};

export const setDeleteProfilePhoto = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};

	try {
		const response = await axios.delete(
			`${import.meta.env.VITE_API_BACKEND}api/users/profile/${id}/photo`,
			config,
		);

		if (response.status === 200 || response.status === 204 || response.status === 202) {
			return { status: "success", data: null };
		} else {
			return {
				status: "error",
				message: "Unexpected server response",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 422:
					return {
						status: "unprocessable_entity",
						errors: error.response.data.errors,
					};
				case 404:
					return { status: "not_found", message: "Resource not found" };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				default:
					return { status: "error", message: "An error occurred" };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};
